import { useState, useMemo } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import _ from 'lodash';
import dayjs from 'dayjs';

// MUI
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';
import { alpha } from '@mui/material/styles';
import { green, red, amber, grey, blue } from '@mui/material/colors';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';

// FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartTreeMap, faCalendarDays, faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons';

import isoWeek from 'dayjs/plugin/isoWeek';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';

// Local
import Header from '../../Header.js';
import { calcAccountScore, getScoreColor, calcOrgMaxRevenue, getScoreExplanation } from '../utils/accountScore.js';
import { opportunitySellAndCost } from '../../utils/opportunityFinancials.js';
import currencyToText from '../../CurrencyToText.js';
import OpportunitiesFilterOwner from '../components/OpportunitiesFilter.owner.js';

dayjs.extend(isoWeek);
dayjs.extend(quarterOfYear);

function SalesManagement() {
    const { org, me, accounts, opportunities, exchangeRates } = useOutletContext();
    const navigate = useNavigate();
    const [period, setPeriod] = useState('quarter');
    const [customFrom, setCustomFrom] = useState(null);
    const [customTo, setCustomTo] = useState(null);
    const [ownerFilter, setOwnerFilter] = useState('all');
    const [activityByFilter, setActivityByFilter] = useState('all');
    // Committed search selection (chips): autocomplete picks are objects { type:'tag'|'name',
    // value }, and free-typed entries are plain strings. Applies only once committed, matching
    // the opportunities-page search.
    const [searchSelection, setSearchSelection] = useState([]);
    const [tagDrilldown, setTagDrilldown] = useState(null);
    const [teamSort, setTeamSort] = useState({ key: 'sent', asc: false });
    const cur = org?.defaults?.currency || 'USD';
    const fraction = new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

    const allOpps = useMemo(() => (opportunities || []).filter(o => o.status !== 'deleted'), [opportunities]);

    const periodDates = useMemo(() => {
        const now = dayjs();
        if (period === 'week') return { start: now.startOf('isoWeek'), end: now.endOf('isoWeek') };
        if (period === 'month') return { start: now.startOf('month'), end: now.endOf('month') };
        if (period === 'quarter') return { start: now.startOf('quarter'), end: now.endOf('quarter') };
        if (period === 'past-week') return { start: now.subtract(1, 'week').startOf('isoWeek'), end: now.subtract(1, 'week').endOf('isoWeek') };
        if (period === 'past-month') return { start: now.subtract(1, 'month').startOf('month'), end: now.subtract(1, 'month').endOf('month') };
        if (period === 'past-quarter') return { start: now.subtract(1, 'quarter').startOf('quarter'), end: now.subtract(1, 'quarter').endOf('quarter') };
        if (period === 'year') return { start: now.startOf('year'), end: now.endOf('year') };
        if (period === 'custom' && customFrom) return { start: dayjs(customFrom).startOf('day'), end: customTo ? dayjs(customTo).endOf('day') : now };
        return null;
    }, [period, customFrom, customTo]);

    const periodStart = periodDates?.start?.valueOf() || null;
    const periodEnd = periodDates?.end?.valueOf() || null;

    // Normalize the committed selection into case-insensitive matchers: tag / name / free-text.
    const searchMatchers = useMemo(() => searchSelection.map(sel =>
        typeof sel === 'string'
            ? { type: 'text', value: sel.trim().toLowerCase() }
            : { type: sel.type, value: String(sel.value).trim().toLowerCase() }
    ).filter(m => m.value), [searchSelection]);

    // Autocomplete suggestions: every distinct tag and account name.
    const searchOptions = useMemo(() => {
        if (!accounts) return [];
        const tagLabels = _(accounts)
            .flatMap(a => [...(a.tags || []), ...(a.updates || []).flatMap(u => u.tags || [])])
            .map(t => t?.label).compact().uniq().sortBy(l => l.toLowerCase()).value();
        const names = _(accounts).map(a => a?.name).compact().uniq().sortBy(n => n.toLowerCase()).value();
        return [
            ...tagLabels.map(t => ({ type: 'tag', value: t })),
            ...names.map(n => ({ type: 'name', value: n })),
        ];
    }, [accounts]);

    // Filter accounts by owner, then by the committed search selection (name or tag)
    const filteredAccounts = useMemo(() => {
        if (!accounts) return [];
        let result = accounts;
        if (ownerFilter !== 'all') {
            const members = ownerFilter === 'me' ? [me.email] : ownerFilter.split(',');
            result = result.filter(a => members.includes(a.owner));
        }
        if (searchMatchers.length) result = result.filter(a => {
            const name = (a.name || '').toLowerCase();
            // Tags: the account's own tags + tags on its updates (like the accounts page).
            const tagLabels = [
                ...(a.tags || []),
                ...(a.updates || []).flatMap(u => u.tags || []),
            ].map(t => t?.label).filter(Boolean).map(l => l.toLowerCase());
            // Every committed matcher must be satisfied (case-insensitive).
            return searchMatchers.every(m => {
                if (m.type === 'tag') return tagLabels.includes(m.value);
                if (m.type === 'name') return name === m.value;
                return name.includes(m.value) || tagLabels.some(l => l.includes(m.value));
            });
        });
        return result;
    }, [accounts, ownerFilter, me, searchMatchers]);

    // Compute scores
    const orgMaxRevenue = useMemo(() => calcOrgMaxRevenue({ accounts: filteredAccounts, opportunities: allOpps, exchangeRates, organization: org }), [filteredAccounts, allOpps, exchangeRates, org]);

    const accountData = useMemo(() => {
        return filteredAccounts.map(account => {
            const acctOpps = allOpps.filter(o => o.account?.id === account.id);
            const score = calcAccountScore({ account, opportunities: acctOpps, exchangeRates, orgMaxRevenue, organization: org });
            const nonCwUpdates = (account.updates || []).filter(u => !u.html?.includes('Account updated in Cargowise') && u.status !== 'rejected');

            // Last contact — includes timeline updates, opp creation, status and pipeline changes
            const timestamps = [
                ...nonCwUpdates.map(u => u.created),
                ...acctOpps.map(o => o.created),
                ...acctOpps.flatMap(o => (o.log || []).filter(e => e.timestamp && (e.type === 'status' || e.type === 'pipeline')).map(e => e.timestamp)),
            ].filter(Boolean);
            const lastContact = timestamps.length > 0 ? _.max(timestamps) : null;
            const daysSince = lastContact ? dayjs().startOf('day').diff(dayjs(lastContact).startOf('day'), 'day') : 999;

            // Period opps
            const periodOpps = periodStart ? acctOpps.filter(o => o.created >= periodStart && (!periodEnd || o.created <= periodEnd)) : acctOpps;
            const wonOpps = periodOpps.filter(o => o.status === 'completed' || o.status === 'endcustomer-accepted');

            // Revenue
            let revenue = 0;
            for (const o of wonOpps) {
                const sell = opportunitySellAndCost({ opportunity: o, exchangeRates, organization: org })?.sell;
                if (isFinite(sell)) revenue += sell;
            }

            return {
                id: account.id,
                name: account.name,
                owner: account.owner,
                score,
                daysSince,
                lastContact,
                periodOpps: periodOpps.length,
                wonCount: wonOpps.length,
                revenue,
                touchpoints: (() => {
                    const sentOpps = (periodStart ? acctOpps.filter(o => o.created >= periodStart && (!periodEnd || o.created <= periodEnd)) : acctOpps)
                        .filter(o => o.status && o.status !== 'draft' && o.status !== 'pre-draft');
                    const updates = periodStart ? nonCwUpdates.filter(u => u.created >= periodStart && (!periodEnd || u.created <= periodEnd)) : nonCwUpdates;
                    return updates.length + sentOpps.length;
                })(),
            };
        });
    }, [filteredAccounts, allOpps, exchangeRates, orgMaxRevenue, org, periodStart, cur]);

    // Aggregated metrics
    const totals = useMemo(() => {
        const memberFilter = ownerFilter === 'all' ? null : ownerFilter === 'me' ? [me.email] : ownerFilter.split(',');
        const periodOpps = allOpps.filter(o => {
            if (periodStart && (o.created < periodStart || (periodEnd && o.created > periodEnd))) return false;
            if (memberFilter && !memberFilter.includes(o.owner)) return false;
            return true;
        });
        const isSent = o => o.status === 'endcustomer-accepted' || o.status === 'completed' || o.status === 'sent-to-endcustomer' || o.status === 'opened-by-customer' || o.status === 'lost' || o.status === 'endcustomer-rejected';
        const sent = periodOpps.filter(isSent);
        const won = periodOpps.filter(o => o.status === 'completed' || o.status === 'endcustomer-accepted');
        const lost = periodOpps.filter(o => o.status === 'lost' || o.status === 'endcustomer-rejected');
        const open = periodOpps.filter(o => o.status === 'sent-to-endcustomer' || o.status === 'opened-by-customer');
        const winRate = sent.length > 0 ? Math.round((won.length / sent.length) * 100) : 0;
        // Touchpoints: timeline updates from filtered accounts + sent opps (aligned with Quotes Sent source)
        const sentOppsCount = sent.length;
        const timelineUpdates = filteredAccounts.reduce((sum, a) => {
            const nonCw = (a.updates || []).filter(u => !u.html?.includes('Account updated in Cargowise') && u.status !== 'rejected');
            const periodUpdates = periodStart ? nonCw.filter(u => u.created >= periodStart && (!periodEnd || u.created <= periodEnd)) : nonCw;
            return sum + periodUpdates.length;
        }, 0);
        const completedTasksCount = filteredAccounts.reduce((sum, a) => {
            const completed = (a.reminders || []).filter(r => r && r.status === 'completed');
            const periodTasks = periodStart ? completed.filter(r => r.dueDate && new Date(r.dueDate).getTime() >= periodStart && (!periodEnd || new Date(r.dueDate).getTime() <= periodEnd)) : completed;
            return sum + periodTasks.length;
        }, 0);
        const totalTouchpoints = timelineUpdates + sentOppsCount + completedTasksCount;

        // Revenue — each won quote's own Sell and Cost (opportunitySellAndCost), as on its page and
        // in Quotation Analytics
        let totalRevenue = 0;
        let totalCosts = 0;
        for (const o of won) {
            const fin = opportunitySellAndCost({ opportunity: o, exchangeRates, organization: org });
            if (!fin) continue;
            if (isFinite(fin.sell)) totalRevenue += fin.sell;
            if (isFinite(fin.cost)) totalCosts += fin.cost;
        }
        const gp = totalRevenue - totalCosts;
        const margin = totalRevenue > 0 ? (gp / totalRevenue) * 100 : 0;

        return { sent: sent.length, won: won.length, lost: lost.length, open: open.length, winRate, totalRevenue, totalCosts, gp, margin, totalTouchpoints, totalOpps: periodOpps.length };
    }, [allOpps, filteredAccounts, periodStart, accountData, exchangeRates, org, ownerFilter, me, periodEnd]);

    // Score distribution
    const scoreDistribution = useMemo(() => {
        const dist = { hot: 0, warm: 0, cool: 0, cold: 0 };
        accountData.forEach(a => {
            const s = a.score.score;
            if (s >= 8) dist.hot++;
            else if (s >= 5) dist.warm++;
            else if (s >= 2) dist.cool++;
            else dist.cold++;
        });
        return dist;
    }, [accountData]);

    // Accounts going cold — had active engagement (2+ touchpoints) but last contact was 30+ days ago
    const goingCold = useMemo(() => {
        return _.sortBy(accountData.filter(a => a.name && a.touchpoints >= 2 && a.daysSince >= 30), a => -a.daysSince);
    }, [accountData]);

    // Top accounts by score
    const topAccounts = useMemo(() => {
        return _.sortBy(accountData.filter(a => a.name), a => -a.score.score).slice(0, 10);
    }, [accountData]);

    // Per-member performance
    const teamPerformance = useMemo(() => {
        const members = {};
        const acctIds = new Set(filteredAccounts.map(a => a.id));
        const periodOpps = (periodStart ? allOpps.filter(o => acctIds.has(o.account?.id) && o.created >= periodStart && (!periodEnd || o.created <= periodEnd)) : allOpps.filter(o => acctIds.has(o.account?.id)));

        // Initialize from org users
        (org?.users || []).forEach(u => {
            members[u.email] = { email: u.email, name: u.email.split('@')[0], sent: 0, won: 0, lost: 0, open: 0, revenue: 0, touchpoints: 0, accounts: 0, newAccounts: 0 };
        });

        // Count per owner
        filteredAccounts.forEach(a => {
            const owner = a.owner;
            if (!owner || !members[owner]) return;
            members[owner].accounts++;
            const isNew = periodStart ? a.created >= periodStart && (!periodEnd || a.created <= periodEnd) : false;
            if (isNew) members[owner].newAccounts++;
        });

        // Opps per owner
        periodOpps.forEach(o => {
            const owner = o.owner || o.createdBy;
            if (!owner || !members[owner]) return;
            if (o.status && o.status !== 'draft' && o.status !== 'pre-draft') members[owner].sent++;
            if (o.status === 'completed' || o.status === 'endcustomer-accepted') members[owner].won++;
            if (o.status === 'lost' || o.status === 'endcustomer-rejected') members[owner].lost++;
            if (o.status === 'sent-to-endcustomer' || o.status === 'opened-by-customer') members[owner].open++;
        });

        // Touchpoints per team member — attributed by who did the work, not account owner
        filteredAccounts.forEach(a => {
            const nonCw = (a.updates || []).filter(u => !u.html?.includes('Account updated in Cargowise') && u.status !== 'rejected');
            const periodUpdates = periodStart ? nonCw.filter(u => u.created >= periodStart && (!periodEnd || u.created <= periodEnd)) : nonCw;

            // Attribute updates by createdBy
            periodUpdates.forEach(u => {
                const by = u.createdBy;
                if (!by || !members[by]) return;
                members[by].touchpoints++;
                if (!members[by].tagBreakdown) members[by].tagBreakdown = {};
                (u.tags || []).forEach(t => {
                    if (!t.label) return;
                    const key = t.label.toLowerCase();
                    if (!members[by].tagBreakdown[key]) members[by].tagBreakdown[key] = { label: t.label, count: 0, entries: [] };
                    members[by].tagBreakdown[key].count++;
                    members[by].tagBreakdown[key].entries.push({
                        accountName: a.name,
                        accountId: a.id,
                        html: u.html,
                        created: u.created,
                        createdBy: u.createdBy,
                    });
                });
            });

            // Attribute sent opps by owner/createdBy
            const acctSentOpps = (periodStart
                ? allOpps.filter(o => o.account?.id === a.id && o.created >= periodStart && (!periodEnd || o.created <= periodEnd))
                : allOpps.filter(o => o.account?.id === a.id)
            ).filter(o => o.status && o.status !== 'draft' && o.status !== 'pre-draft');
            acctSentOpps.forEach(o => {
                const by = o.owner || o.createdBy;
                if (by && members[by]) members[by].touchpoints++;
            });

            // Attribute completed tasks by assignee
            const completedTasks = (a.reminders || []).filter(r => r && r.status === 'completed');
            const periodTasks = periodStart ? completedTasks.filter(r => r.dueDate && new Date(r.dueDate).getTime() >= periodStart && (!periodEnd || new Date(r.dueDate).getTime() <= periodEnd)) : completedTasks;
            periodTasks.forEach(r => {
                const by = r.assignee || r.createdBy || a.owner;
                if (by && members[by]) members[by].touchpoints++;
            });
        });

        // Revenue — attributed by opportunity owner (who won the deal)
        const revenueAcctIds = new Set(filteredAccounts.map(a => a.id));
        const wonOpps = periodOpps.filter(o => revenueAcctIds.has(o.account?.id) && (o.status === 'completed' || o.status === 'endcustomer-accepted'));
        wonOpps.forEach(o => {
            const by = o.owner || o.createdBy;
            if (!by || !members[by]) return;
            const sell = opportunitySellAndCost({ opportunity: o, exchangeRates, organization: org })?.sell;
            if (isFinite(sell)) members[by].revenue += sell;
        });

        const activeMembers = Object.values(members).filter(m => m.accounts > 0 || m.sent > 0 || m.touchpoints > 0);
        if (activityByFilter === 'all') return _.sortBy(activeMembers, m => -m.sent);
        const activityMembers = activityByFilter === 'me' ? [me.email] : activityByFilter.split(',');
        return _.sortBy(activeMembers.filter(m => activityMembers.includes(m.email)), m => -m.sent);
    }, [filteredAccounts, allOpps, periodStart, org, accountData, activityByFilter, me]);

    // Activity types from timeline tags (across all filtered accounts)
    const activityTypes = useMemo(() => {
        const tagCounts = {};
        filteredAccounts.forEach(a => {
            const nonCw = (a.updates || []).filter(u => !u.html?.includes('Account updated in Cargowise') && u.status !== 'rejected');
            const periodUpdates = periodStart ? nonCw.filter(u => u.created >= periodStart && (!periodEnd || u.created <= periodEnd)) : nonCw;
            periodUpdates.forEach(u => (u.tags || []).forEach(t => {
                if (!t.label) return;
                const key = t.label.toLowerCase();
                tagCounts[key] = tagCounts[key] || { label: t.label, count: 0 };
                tagCounts[key].count++;
            }));
        });
        return _.sortBy(Object.values(tagCounts), t => -t.count).slice(0, 12);
    }, [filteredAccounts, periodStart]);

    // New vs lost business
    const newVsLost = useMemo(() => {
        const newAccounts = periodStart ? filteredAccounts.filter(a => a.created >= periodStart && (!periodEnd || a.created <= periodEnd)).length : filteredAccounts.length;
        const wonAccounts = filteredAccounts.filter(a => {
            const pipeline = (a.tags || []).find(t => t.label?.startsWith('Pipeline: '))?.label?.replace('Pipeline: ', '') || 'New';
            return pipeline === 'Won';
        }).length;
        const inactiveAccounts = filteredAccounts.filter(a => a.inactive).length;
        const coldAccounts = accountData.filter(a => a.name && a.touchpoints >= 2 && a.daysSince >= 30).length;
        return { newAccounts, wonAccounts, inactiveAccounts, coldAccounts };
    }, [filteredAccounts, periodStart, accountData, periodEnd]);

    if (!accounts || !opportunities) return null;

    const distTotal = scoreDistribution.hot + scoreDistribution.warm + scoreDistribution.cool + scoreDistribution.cold || 1;

    return (
        <Box sx={{ paddingBottom: '36px', background: '#fafbfc' }}>
            <Header icon={faChartTreeMap} title={<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>Sales Management<Box sx={{ fontSize: '10px', fontWeight: 700, color: '#3b82f6', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px', px: '6px', py: '1px' }}>Beta</Box></Box>} />

            <Box sx={{ padding: '0 24px' }}>
                {/* Monada Score explanation */}
                <Paper sx={{ p: 2.5, mb: 2, background: '#f8fafc', border: '1px solid #e2e8f0' }} elevation={0}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#334155', mb: 1 }}>How the Monada Score Works</Typography>
                    <Typography sx={{ fontSize: '12px', color: '#64748b', lineHeight: 1.7 }}>
                        Each account gets a score from <b>0 to 10</b> based on four factors:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 3, mt: 1.5, flexWrap: 'wrap' }}>
                        {[
                            { weight: '30%', title: 'Activity', desc: 'Timeline updates and sent quotes in the last 90 days (excludes drafts and CargoWise syncs). 5+ touchpoints reach full score.', color: blue[600] },
                            { weight: '25%', title: 'Win Rate', desc: 'Won deals vs lost deals. Accounts with open deals awaiting response get partial credit. A high conversion rate signals a strong relationship.', color: green[600] },
                            { weight: '25%', title: 'Revenue', desc: 'Total revenue from won deals, scaled logarithmically so one large account doesn\'t flatten everyone else.', color: amber[700] },
                            { weight: '20%', title: 'Recency', desc: 'How recently you were in touch. Score gradually drops over 6 months without contact.', color: red[500] },
                        ].map(f => (
                            <Box key={f.title} sx={{ flex: 1, minWidth: '140px' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: f.color }} />
                                    <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>{f.title}</Typography>
                                    <Typography sx={{ fontSize: '10px', color: '#94a3b8' }}>({f.weight})</Typography>
                                </Box>
                                <Typography sx={{ fontSize: '11px', color: '#64748b', lineHeight: 1.5 }}>{f.desc}</Typography>
                            </Box>
                        ))}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, mt: 1.5 }}>
                        {[
                            { label: 'Hot', range: '8-10', color: '#16a34a', bg: '#f0fdf4' },
                            { label: 'Warm', range: '5-7', color: '#d97706', bg: '#fffbeb' },
                            { label: 'Cool', range: '2-4', color: '#ea580c', bg: '#fff7ed' },
                            { label: 'Cold', range: '0-1', color: '#dc2626', bg: '#fef2f2' },
                        ].map(s => (
                            <Box key={s.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.3, borderRadius: '8px', background: s.bg }}>
                                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: s.color }}>{s.label}</Typography>
                                <Typography sx={{ fontSize: '10px', color: s.color }}>({s.range})</Typography>
                            </Box>
                        ))}
                    </Box>
                </Paper>

                {/* Filters */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <Tooltip title="Filter accounts by ownership — controls which accounts are included in all metrics" placement="bottom" arrow>
                    <Box sx={{ maxWidth: '200px' }}>
                        <OpportunitiesFilterOwner
                            org={org}
                            me={me}
                            filter={{ members: ownerFilter === 'all' ? [] : ownerFilter === 'me' ? [me.email] : ownerFilter.split(',') }}
                            setFilter={(f) => {
                                if (!f.members || f.members.length === 0) setOwnerFilter('all');
                                else if (f.members.length === 1 && f.members[0] === me.email) setOwnerFilter('me');
                                else setOwnerFilter(f.members.join(','));
                            }}
                            noMarginRight
                            noSelectionText='All Accounts'
                            mineText='My accounts'
                        />
                    </Box>
                    </Tooltip>
                    <Tooltip title="Filter team table by who performed the activity — shows only selected members' touchpoints, quotes, and tasks" placement="bottom" arrow>
                    <Box sx={{ maxWidth: '200px' }}>
                        <OpportunitiesFilterOwner
                            org={org}
                            me={me}
                            filter={{ members: activityByFilter === 'all' ? [] : activityByFilter === 'me' ? [me.email] : activityByFilter.split(',') }}
                            setFilter={(f) => {
                                if (!f.members || f.members.length === 0) setActivityByFilter('all');
                                else if (f.members.length === 1 && f.members[0] === me.email) setActivityByFilter('me');
                                else setActivityByFilter(f.members.join(','));
                            }}
                            noMarginRight
                            noSelectionText='All Activity'
                            mineText='My activity'
                        />
                    </Box>
                    </Tooltip>
                    <Select
                        size='small'
                        value={period}
                        onChange={(e) => setPeriod(e.target.value)}
                        renderValue={(val) => {
                            const labels = { week: 'This Week', month: 'This Month', quarter: 'This Quarter', 'past-week': 'Last Week', 'past-month': 'Last Month', 'past-quarter': 'Last Quarter', year: 'This Year', custom: 'Custom Range', all: 'All Time' };
                            return (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <FontAwesomeIcon icon={faCalendarDays} style={{ fontSize: '12px', opacity: 0.5 }} />
                                    <span>{labels[val] || val}</span>
                                    {periodDates && (
                                        <Typography component='span' sx={{ fontSize: '10px', opacity: 0.6, ml: '2px' }}>
                                            ({periodDates.start.format('ddd, MMM D')} – {periodDates.end.format('ddd, MMM D, YYYY')})
                                        </Typography>
                                    )}
                                </Box>
                            );
                        }}
                        sx={{ fontSize: '12px', borderRadius: '8px', backgroundColor: '#fff', '& .MuiSelect-select': { py: '5px', pr: '28px !important' }, minWidth: 0 }}
                        MenuProps={{ style: { zIndex: 1000000 } }}
                    >
                        <MenuItem value='week'>This Week</MenuItem>
                        <MenuItem value='month'>This Month</MenuItem>
                        <MenuItem value='quarter'>This Quarter</MenuItem>
                        <MenuItem value='past-week'>Last Week</MenuItem>
                        <MenuItem value='past-month'>Last Month</MenuItem>
                        <MenuItem value='past-quarter'>Last Quarter</MenuItem>
                        <MenuItem value='year'>This Year</MenuItem>
                        <MenuItem value='custom'>Custom Range</MenuItem>
                        <MenuItem value='all'>All Time</MenuItem>
                    </Select>
                    {period === 'custom' && (
                        <>
                            <MobileDatePicker
                                label='From'
                                inputFormat='DD/MM/YYYY'
                                value={customFrom}
                                onChange={(v) => setCustomFrom(v)}
                                renderInput={(params) => <TextField size='small' sx={{ width: 140, '& .MuiInputBase-root': { fontSize: '12px', borderRadius: '8px', backgroundColor: '#fff' } }} {...params} />}
                            />
                            <MobileDatePicker
                                label='To'
                                inputFormat='DD/MM/YYYY'
                                value={customTo}
                                onChange={(v) => setCustomTo(v)}
                                renderInput={(params) => <TextField size='small' sx={{ width: 140, '& .MuiInputBase-root': { fontSize: '12px', borderRadius: '8px', backgroundColor: '#fff' } }} {...params} />}
                            />
                        </>
                    )}
                </Box>

                {/* Search — its own full-width row, below the filters. Autocomplete suggests
                    existing tags and account names; pick one or type free text and press Enter. */}
                <Tooltip title="Search by account or tag — pick a suggestion or press Enter. Filters which accounts are included in all metrics" placement="bottom" arrow>
                    <Autocomplete
                        multiple
                        freeSolo
                        size='small'
                        options={searchOptions}
                        value={searchSelection}
                        onChange={(e, value) => setSearchSelection(value)}
                        getOptionLabel={o => typeof o === 'string' ? o : o.value}
                        groupBy={o => o.type === 'tag' ? 'Tags' : 'Accounts'}
                        isOptionEqualToValue={(o, v) => typeof o !== 'string' && typeof v !== 'string' && o.type === v.type && o.value === v.value}
                        renderOption={(props, option) => (
                            <li {...props} key={`${option.type}:${option.value}`}>
                                <Typography component='span' sx={{ fontSize: '10px', fontWeight: 700, color: option.type === 'tag' ? '#7c3aed' : '#2563eb', minWidth: '58px' }}>
                                    {option.type === 'tag' ? 'TAG' : 'ACCOUNT'}
                                </Typography>
                                <Typography component='span' sx={{ fontSize: '12px' }}>{option.value}</Typography>
                            </li>
                        )}
                        sx={{ mb: 2 }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={searchSelection.length ? '' : 'Search accounts & tags…'}
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                        <>
                                            <InputAdornment position='start' sx={{ ml: '4px' }}>
                                                <FontAwesomeIcon icon={faMagnifyingGlass} style={{ fontSize: '12px', opacity: 0.4 }} />
                                            </InputAdornment>
                                            {params.InputProps.startAdornment}
                                        </>
                                    ),
                                }}
                                sx={{ '& .MuiInputBase-root': { fontSize: '12px', borderRadius: '8px', backgroundColor: '#fff' } }}
                            />
                        )}
                    />
                </Tooltip>

                {/* Top KPIs */}
                <Paper sx={{ p: 3, mb: 2 }} elevation={0}>
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        {[
                            { label: 'Total Accounts', value: filteredAccounts.length, color: 'text.secondary', tip: 'All accounts owned by the selected team members' },
                            { label: 'Active Accounts', value: accountData.filter(a => a.touchpoints > 0).length, color: 'text.primary', tip: 'Accounts with at least one touchpoint (timeline update, sent quote, or completed task) in this period' },
                            { label: 'New Accounts', value: newVsLost.newAccounts, color: newVsLost.newAccounts > 0 ? green[700] : 'text.secondary', tip: 'Accounts created during this period' },
                            { label: 'Touchpoints', value: totals.totalTouchpoints, color: 'text.primary', tip: 'Total interactions — timeline updates, sent quotes/contracts, and completed tasks (excludes drafts and CargoWise syncs)' },
                            { label: 'Quotes Sent', value: totals.sent, color: blue[700], tip: 'Opportunities sent to customers' },
                            { label: 'Won', value: totals.won, color: green[700], tip: 'Deals accepted by customer' },
                            { label: 'Lost', value: totals.lost, color: red[500], tip: 'Deals rejected by customer' },
                            { label: 'Open', value: totals.open, color: amber[800], tip: 'Deals awaiting customer response' },
                            { label: 'Win Rate', value: `${totals.winRate}%`, color: totals.winRate >= 50 ? green[700] : totals.winRate >= 25 ? amber[800] : red[500], tip: 'Won quotes out of all sent quotes — includes pending quotes in the denominator' },
                            { label: 'Revenue', value: currencyToText(cur, true, fraction.format(totals.totalRevenue)), color: 'text.primary', tip: 'Total selling value from won deals' },
                        ].map(kpi => (
                            <Tooltip key={kpi.label} title={kpi.tip || ''} arrow placement='top'>
                                <Box sx={{ flex: 1, minWidth: '90px', cursor: 'help' }}>
                                    <Typography sx={{ fontSize: '10px', color: '#94a3b8', fontWeight: 500 }}>{kpi.label}</Typography>
                                    <Typography sx={{ fontSize: '20px', fontWeight: 700, color: kpi.color }}>{kpi.value}</Typography>
                                </Box>
                            </Tooltip>
                        ))}
                    </Box>
                </Paper>

                {/* Team Performance Table */}
                {teamPerformance.length > 0 && (
                    <Paper sx={{ p: 3, mb: 2 }} elevation={0}>
                        <Typography sx={{ fontSize: '11px', color: blue[600], fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2 }}>
                            Team Performance
                        </Typography>
                        <Box sx={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                <thead>
                                    <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                                        {[
                                            { label: 'Team Member', key: 'name', tip: 'Account owner — the team member responsible for this account' },
                                            { label: 'Accounts', key: 'accounts', tip: 'Total accounts owned by this team member' },
                                            { label: 'Touchpoints', key: 'touchpoints', tip: 'Timeline updates, sent quotes/contracts, and completed tasks — attributed by who did the work, not account owner. Tags show the breakdown by activity type.' },
                                            { label: 'New Accounts', key: 'newAccounts', tip: 'Accounts created during the selected period' },
                                        ].map(h => (
                                            <Tooltip key={h.label} title={h.tip} arrow placement='top'>
                                                <td
                                                    style={{ padding: '8px 12px', fontWeight: 700, color: teamSort.key === h.key ? '#1976d2' : '#64748b', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em', cursor: 'pointer', userSelect: 'none' }}
                                                    onClick={() => setTeamSort(prev => prev.key === h.key ? { key: h.key, asc: !prev.asc } : { key: h.key, asc: false })}
                                                >
                                                    {h.label} {teamSort.key === h.key ? (teamSort.asc ? '↑' : '↓') : ''}
                                                </td>
                                            </Tooltip>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {_.orderBy(teamPerformance.map(m => ({ ...m, winRate: m.sent > 0 ? Math.round((m.won / m.sent) * 100) : 0 })), [teamSort.key], [teamSort.asc ? 'asc' : 'desc']).map(m => {
                                        const wr = m.winRate;
                                        return (
                                            <tr key={m.email} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                <td style={{ padding: '8px 12px', fontWeight: 600 }}>{m.name}</td>
                                                <td style={{ padding: '8px 12px' }}>{m.accounts}</td>
                                                <td style={{ padding: '8px 12px' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                                        <Typography sx={{ fontSize: '12px', fontWeight: m.touchpoints > 0 ? 600 : 400, color: m.touchpoints > 10 ? green[700] : m.touchpoints > 0 ? '#334155' : '#94a3b8' }}>{m.touchpoints}</Typography>
                                                        {m.tagBreakdown && _.sortBy(Object.values(m.tagBreakdown), t => -t.count).slice(0, 5).map(t => (
                                                            <Box key={t.label}
                                                                onClick={() => setTagDrilldown({ memberName: m.name, tagLabel: t.label, entries: t.entries })}
                                                                sx={{ display: 'inline-flex', alignItems: 'center', gap: '2px', px: '5px', py: '1px', borderRadius: '8px', background: blue[50], border: `1px solid ${blue[100]}`, cursor: 'pointer', '&:hover': { background: blue[100] } }}>
                                                                <Typography sx={{ fontSize: '9px', color: blue[800] }}>{t.label}</Typography>
                                                                <Typography sx={{ fontSize: '8px', fontWeight: 700, color: blue[500] }}>{t.count}</Typography>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                </td>
                                                <td style={{ padding: '8px 12px', fontWeight: m.newAccounts > 0 ? 600 : 400, color: m.newAccounts > 0 ? green[700] : '#94a3b8' }}>{m.newAccounts}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </Box>
                    </Paper>
                )}

                {/* Activity Distribution + Activity Types + New vs Lost */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    {/* Activity per user — donut */}
                    {teamPerformance.length > 0 && (
                        <Paper sx={{ p: 3, flex: 1 }} elevation={0}>
                            <Typography sx={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2 }}>
                                Activity per Team Member
                            </Typography>
                            {(() => {
                                const totalTP = _.sum(teamPerformance.map(m => m.touchpoints)) || 1;
                                const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'];
                                let cumAngle = 0;
                                const segments = teamPerformance.filter(m => m.touchpoints > 0).map((m, i) => {
                                    const pct = m.touchpoints / totalTP;
                                    const startAngle = cumAngle;
                                    cumAngle += pct * 360;
                                    return { ...m, pct, startAngle, endAngle: cumAngle, color: colors[i % colors.length] };
                                });
                                const size = 140;
                                const cx = size / 2, cy = size / 2, r = 55, inner = 35;
                                function arc(startDeg, endDeg, radius) {
                                    const s = (startDeg - 90) * Math.PI / 180;
                                    const e = (endDeg - 90) * Math.PI / 180;
                                    const x1 = cx + radius * Math.cos(s);
                                    const y1 = cy + radius * Math.sin(s);
                                    const x2 = cx + radius * Math.cos(e);
                                    const y2 = cy + radius * Math.sin(e);
                                    const large = endDeg - startDeg > 180 ? 1 : 0;
                                    return `M ${x1} ${y1} A ${radius} ${radius} 0 ${large} 1 ${x2} ${y2}`;
                                }
                                return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                                            {segments.map((s, i) => (
                                                <path key={i}
                                                    d={`${arc(s.startAngle, s.endAngle, r)} L ${cx + inner * Math.cos((s.endAngle - 90) * Math.PI / 180)} ${cy + inner * Math.sin((s.endAngle - 90) * Math.PI / 180)} ${arc(s.endAngle, s.startAngle, inner).replace('M', 'L')} Z`}
                                                    fill={s.color} stroke='#fff' strokeWidth='1.5'
                                                />
                                            ))}
                                            <text x={cx} y={cy - 5} textAnchor='middle' fontSize='16' fontWeight='700' fill='#334155'>{totalTP}</text>
                                            <text x={cx} y={cy + 10} textAnchor='middle' fontSize='9' fill='#94a3b8'>touchpoints</text>
                                        </svg>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            {segments.map(s => (
                                                <Box key={s.email} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
                                                    <Typography sx={{ fontSize: '11px', color: '#334155' }}>{s.name}</Typography>
                                                    <Typography sx={{ fontSize: '10px', color: '#94a3b8' }}>({s.touchpoints} · {Math.round(s.pct * 100)}%)</Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                );
                            })()}
                        </Paper>
                    )}
                    {/* Activity Types */}
                    <Paper sx={{ p: 3, flex: 1 }} elevation={0}>
                        <Typography sx={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2 }}>
                            Activity Types
                        </Typography>
                        {activityTypes.length === 0 ? (
                            <Typography sx={{ fontSize: '12px', color: '#94a3b8' }}>No tagged activities in this period</Typography>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {(() => {
                                    const maxCount = activityTypes[0]?.count || 1;
                                    return activityTypes.map(t => (
                                        <Box key={t.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography sx={{ fontSize: '11px', fontWeight: 500, minWidth: '80px', textAlign: 'right', color: '#334155' }}>{t.label}</Typography>
                                            <Box sx={{ flex: 1, height: '16px', background: grey[100], borderRadius: '4px', overflow: 'hidden' }}>
                                                <Box sx={{ width: `${(t.count / maxCount) * 100}%`, height: '100%', background: blue[400], borderRadius: '4px', minWidth: '2px' }} />
                                            </Box>
                                            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#334155', minWidth: '24px' }}>{t.count}</Typography>
                                        </Box>
                                    ));
                                })()}
                            </Box>
                        )}
                    </Paper>
                    {/* New vs Lost Business */}
                    <Paper sx={{ p: 3, flex: 1 }} elevation={0}>
                        <Typography sx={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2 }}>
                            New vs Lost Business
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {[
                                { label: 'New Accounts', value: newVsLost.newAccounts, color: green[600], icon: '↑' },
                                { label: 'Won Accounts', value: newVsLost.wonAccounts, color: green[700], icon: '✓' },
                                { label: 'Inactive Accounts', value: newVsLost.inactiveAccounts, color: red[500], icon: '✗' },
                                { label: 'Accounts Going Cold', value: newVsLost.coldAccounts, color: red[400], icon: '↓' },
                            ].map(item => (
                                <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography sx={{ fontSize: '16px', color: item.color }}>{item.icon}</Typography>
                                        <Typography sx={{ fontSize: '12px', color: '#334155' }}>{item.label}</Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: '20px', fontWeight: 700, color: item.color }}>{item.value}</Typography>
                                </Box>
                            ))}
                        </Box>
                        <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${grey[200]}` }}>
                            <Typography sx={{ fontSize: '10px', color: '#94a3b8' }}>
                                Net: {newVsLost.wonAccounts - newVsLost.inactiveAccounts >= 0 ? '+' : ''}{newVsLost.wonAccounts - newVsLost.inactiveAccounts} accounts
                                {newVsLost.wonAccounts - newVsLost.inactiveAccounts >= 0
                                    ? <span style={{ color: green[600] }}> — growing</span>
                                    : <span style={{ color: red[500] }}> — shrinking</span>}
                            </Typography>
                        </Box>
                    </Paper>
                </Box>

                {/* Score Distribution + Needs Attention */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    {/* Score Distribution */}
                    <Paper sx={{ p: 3, flex: 1 }} elevation={0}>
                        <Typography sx={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2 }}>
                            Account Health Distribution
                        </Typography>
                        <Typography sx={{ fontSize: '10px', color: '#94a3b8', mb: 1 }}>Based on overall account health, not limited to the selected period</Typography>
                        {/* Visual bar */}
                        <Box sx={{ display: 'flex', height: '32px', borderRadius: '8px', overflow: 'hidden', mb: 2 }}>
                            {[
                                { key: 'hot', count: scoreDistribution.hot, color: '#16a34a', label: 'Hot' },
                                { key: 'warm', count: scoreDistribution.warm, color: '#d97706', label: 'Warm' },
                                { key: 'cool', count: scoreDistribution.cool, color: '#ea580c', label: 'Cool' },
                                { key: 'cold', count: scoreDistribution.cold, color: '#dc2626', label: 'Cold' },
                            ].filter(s => s.count > 0).map(s => (
                                <Tooltip key={s.key} title={`${s.label}: ${s.count} accounts (${Math.round(s.count / distTotal * 100)}%)`} arrow>
                                    <Box sx={{
                                        width: `${(s.count / distTotal) * 100}%`,
                                        background: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        cursor: 'pointer', transition: 'opacity 0.2s', '&:hover': { opacity: 0.8 },
                                    }}>
                                        {s.count / distTotal > 0.08 && (
                                            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#fff' }}>{s.count}</Typography>
                                        )}
                                    </Box>
                                </Tooltip>
                            ))}
                        </Box>
                        {/* Legend */}
                        <Box sx={{ display: 'flex', gap: 3 }}>
                            {[
                                { label: 'Hot (8-10)', count: scoreDistribution.hot, color: '#16a34a' },
                                { label: 'Warm (5-7)', count: scoreDistribution.warm, color: '#d97706' },
                                { label: 'Cool (2-4)', count: scoreDistribution.cool, color: '#ea580c' },
                                { label: 'Cold (0-1)', count: scoreDistribution.cold, color: '#dc2626' },
                            ].map(s => (
                                <Box key={s.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: s.color }} />
                                    <Typography sx={{ fontSize: '11px', color: '#64748b' }}>{s.label}: <b>{s.count}</b></Typography>
                                </Box>
                            ))}
                        </Box>
                    </Paper>

                    {/* Accounts Going Cold */}
                    <Paper sx={{ p: 3, flex: 1, maxHeight: '250px', overflow: 'hidden' }} elevation={0}>
                        <Tooltip title='Accounts that had active engagement (2+ touchpoints) but no user-driven activity in 30+ days. Sorted by most urgent first. Excludes accounts with no prior engagement and automatic CW1 updates.' arrow placement='top'>
                            <Typography sx={{ fontSize: '11px', color: amber[800], fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5, cursor: 'help' }}>
                                Accounts Going Cold
                            </Typography>
                        </Tooltip>
                        <Typography sx={{ fontSize: '10px', color: '#94a3b8', mb: 1 }}>Had engagement but no contact in 30+ days — most urgent first</Typography>
                        <Box sx={{ maxHeight: '190px', overflowY: 'auto' }}>
                            {goingCold.length === 0 && (
                                <Typography sx={{ fontSize: '12px', color: '#94a3b8' }}>All accounts are up to date</Typography>
                            )}
                            {goingCold.slice(0, 15).map(a => {
                                const sc = getScoreColor(a.score.score);
                                return (
                                    <Box key={a.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, cursor: 'pointer', '&:hover': { background: grey[50] }, borderRadius: '4px', px: 0.5 }}
                                        onClick={() => navigate(`../accounts/${a.id}/details`)}
                                    >
                                        <Box sx={{ minWidth: '24px', height: '20px', borderRadius: '10px', background: sc.bg, border: `1px solid ${sc.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Typography sx={{ fontSize: '10px', fontWeight: 800, color: sc.color }}>{a.score.score}</Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: '12px', fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</Typography>
                                        <Typography sx={{ fontSize: '11px', fontWeight: 600, color: a.daysSince > 30 ? red[500] : amber[700] }}>{a.daysSince}d</Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Paper>
                </Box>

                {/* Top Accounts + Coldest Accounts */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    {/* Top Accounts */}
                    <Paper sx={{ p: 3, flex: 1 }} elevation={0}>
                        <Tooltip title='Accounts ranked by Monada Score — a composite of engagement frequency, quote activity, win rate, and recency of contact. Higher scores indicate healthier, more active accounts.' arrow placement='top'>
                            <Typography sx={{ fontSize: '11px', color: green[600], fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5, cursor: 'help' }}>
                                Top Accounts
                            </Typography>
                        </Tooltip>
                        <Typography sx={{ fontSize: '10px', color: '#94a3b8', mb: 1 }}>Based on overall account health, not limited to the selected period</Typography>
                        {topAccounts.map((a, i) => {
                            const sc = getScoreColor(a.score.score);
                            return (
                                <Tooltip key={a.id} arrow placement='left' title={
                                    <Box sx={{ fontSize: '10px' }}>
                                        {getScoreExplanation(a.score).map((line, j) => (
                                            <Box key={j} sx={{ fontWeight: j === 0 ? 700 : 400, mb: j === 0 ? 0.5 : 0 }}>{line}</Box>
                                        ))}
                                    </Box>
                                }>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, cursor: 'pointer', '&:hover': { background: grey[50] }, borderRadius: '4px', px: 0.5 }}
                                        onClick={() => navigate(`../accounts/${a.id}/details`)}
                                    >
                                        <Typography sx={{ fontSize: '10px', color: '#94a3b8', minWidth: '16px' }}>{i + 1}.</Typography>
                                        <Box sx={{ minWidth: '24px', height: '20px', borderRadius: '10px', background: sc.bg, border: `1px solid ${sc.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Typography sx={{ fontSize: '10px', fontWeight: 800, color: sc.color }}>{a.score.score}</Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: '12px', fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</Typography>
                                        <Typography sx={{ fontSize: '10px', color: '#94a3b8' }}>{a.wonCount} won · {a.touchpoints} touches</Typography>
                                    </Box>
                                </Tooltip>
                            );
                        })}
                    </Paper>

                </Box>

            </Box>

            {/* Tag drilldown dialog */}
            <Dialog open={!!tagDrilldown} onClose={() => setTagDrilldown(null)} maxWidth='md' fullWidth>
                {tagDrilldown && (
                    <>
                        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography sx={{ fontSize: '16px', fontWeight: 700 }}>
                                    {tagDrilldown.memberName} — "{tagDrilldown.tagLabel}" ({tagDrilldown.entries.length})
                                </Typography>
                                <Typography sx={{ fontSize: '12px', color: '#94a3b8' }}>
                                    Timeline updates tagged with "{tagDrilldown.tagLabel}"
                                </Typography>
                            </Box>
                        </DialogTitle>
                        <DialogContent>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                <thead>
                                    <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                                        <td style={{ padding: '8px 12px', fontWeight: 700, color: '#64748b', fontSize: '10px', textTransform: 'uppercase', width: '140px' }}>Date</td>
                                        <td style={{ padding: '8px 12px', fontWeight: 700, color: '#64748b', fontSize: '10px', textTransform: 'uppercase', width: '180px' }}>Account</td>
                                        <td style={{ padding: '8px 12px', fontWeight: 700, color: '#64748b', fontSize: '10px', textTransform: 'uppercase' }}>Update</td>
                                        <td style={{ padding: '8px 12px', fontWeight: 700, color: '#64748b', fontSize: '10px', textTransform: 'uppercase', width: '100px' }}>By</td>
                                    </tr>
                                </thead>
                                <tbody>
                                    {_.sortBy(tagDrilldown.entries, e => -e.created).map((entry, i) => (
                                        <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                                            onClick={() => { setTagDrilldown(null); navigate(`../accounts/${entry.accountId}/details`); }}
                                        >
                                            <td style={{ padding: '8px 12px', color: '#64748b', fontSize: '11px', whiteSpace: 'nowrap' }}>
                                                {dayjs(entry.created).format('MMM D, YYYY')}
                                                <br />
                                                <span style={{ fontSize: '10px', color: '#94a3b8' }}>{dayjs(entry.created).format('h:mm A')}</span>
                                            </td>
                                            <td style={{ padding: '8px 12px', fontWeight: 500 }}>
                                                <Typography sx={{ fontSize: '12px', color: blue[700], '&:hover': { textDecoration: 'underline' } }}>{entry.accountName}</Typography>
                                            </td>
                                            <td style={{ padding: '8px 12px' }}>
                                                <Box sx={{ fontSize: '11px', color: '#334155', maxHeight: '80px', overflowY: 'auto', '& p': { m: 0 }, '& h1, & h2, & h3': { fontSize: '11px', m: 0 } }}
                                                    dangerouslySetInnerHTML={{ __html: entry.html || '<i style="color:#94a3b8">Tags only</i>' }}
                                                />
                                            </td>
                                            <td style={{ padding: '8px 12px', fontSize: '11px', color: '#94a3b8' }}>
                                                {(entry.createdBy || '').split('@')[0]}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </DialogContent>
                    </>
                )}
            </Dialog>
        </Box>
    );
}

export default SalesManagement;
